/**
 * USI-008 (ADR-011): per-kind body for the `bank-line` triage item.
 *
 * Renders a 1-row summary at top + a tile picker below. The tile set
 * depends only on debit vs credit (per Item 7 deep-review / ADR-011, the
 * audited-model `accountType` discriminator is gone — every
 * BankTransaction is on the campaign's checking account by definition).
 * ADR-014 collapsed the retired Transfer / Not-campaign tiles into one
 * muted "Exclude" off-ramp (always last; the money paths lead):
 *
 *   | Row type | Pick tiles              |
 *   |----------|-------------------------|
 *   | Debit    | Expense · Exclude       |
 *   | Credit   | Contribution · Exclude  |
 *
 * After a tile click (or D/E/X keyboard pick handled by the modal's
 * `useFinanceKeyboardShortcuts` triage scope), the matching
 * `BankTransactionClassificationPanel` mounts inline with
 * `initialSelectedType` pre-set. The panel handles all the form,
 * mutation, gate, and validation orchestration — this body is a thin
 * tile-picker shell around the existing classification component, per
 * the USI-008 AC.
 *
 * Per ADR-011 commitment #11 (no linked-pair routing) the modal advances
 * chronologically after classify; the freshly-created Pending row will
 * surface in the next refetch like any other row.
 */
import { assertNever, type TriageItem } from '@signalvote/shared';
import { suggestedKind, type SuggestedKind } from '@/lib/finance/triageSuggest';
// Imported by direct file path, NOT the `close` barrel: the barrel re-exports
// TriageModal, which imports this file, so `@/components/finance/close` here would
// close an index → TriageModal → BankLineBody → index import cycle. The leaf path
// sidesteps it (root Golden Rules: a documented standard yields to local context).
import { BankLineInfoHeader } from '@/components/finance/close/BankLineInfoHeader';
import {
  BankTransactionClassificationPanel,
  type BankTransactionPanelRow,
} from '@/components/finance/classification';
import type { TriagePickerTile } from '@/components/finance/useFinanceKeyboardShortcuts';
import { formatAmount } from '@/components/finance/utils';
import { cn } from '@/lib/utils';

interface BankLineBodyProps {
  item: Extract<TriageItem, { kind: 'BankLine' }>;
  /** Lifted by `TriageModal` so keyboard D/E/X + click share state. */
  pickedTile: TriagePickerTile | null;
  /** Click / keyboard pick handler — `TriageModal` resets per item. */
  onPickTile: (tile: TriagePickerTile) => void;
  /** Fired by the embedded panel after a successful classify mutation. */
  onClassified: () => void;
  /** Fired by the embedded panel's close affordance — closes the modal. */
  onClose: () => void;
}

/** Map the modal's tile discriminator → the panel's `selectedType`. */
function tileToSelectedType(
  tile: TriagePickerTile,
): 'contribution' | 'expenditure' | 'exclude' {
  switch (tile) {
    case 'contribution':
      return 'contribution';
    case 'expense':
      return 'expenditure';
    case 'exclude':
      return 'exclude';
    default:
      return assertNever(tile);
  }
}

/**
 * Map `triageSuggest.suggestedKind` → `TriagePickerTile`. The two
 * discriminators share their values 1:1 post-ADR-014 (the retired
 * `'dismiss'` → `'not-campaign'` divergence collapsed into `'exclude'`).
 * A new value in either union fails this switch at compile, forcing the
 * mapping to be revisited explicitly.
 */
function suggestedToTile(s: SuggestedKind | null): TriagePickerTile | null {
  if (s === null) return null;
  switch (s) {
    case 'contribution':
      return 'contribution';
    case 'expense':
      return 'expense';
    case 'exclude':
      return 'exclude';
    default:
      return assertNever(s);
  }
}

interface TileDef {
  tile: TriagePickerTile;
  label: string;
  /** Single-key shortcut shown on the tile; omitted for click-only tiles. */
  keyHint?: string;
}

// ADR-014: the muted "Exclude" off-ramp, always rendered LAST and visually
// secondary (dashed border, smaller) so the money paths lead. `X` key.
const EXCLUDE_TILE: TileDef = { tile: 'exclude', label: 'Exclude', keyHint: 'X' };

const DEBIT_TILES: ReadonlyArray<TileDef> = [
  { tile: 'expense', label: 'Expense', keyHint: 'E' },
  EXCLUDE_TILE,
];

const CREDIT_TILES: ReadonlyArray<TileDef> = [
  { tile: 'contribution', label: 'Contribution', keyHint: 'D' },
  EXCLUDE_TILE,
];

export function BankLineBody({
  item,
  pickedTile,
  onPickTile,
  onClassified,
  onClose,
}: BankLineBodyProps) {
  const { bt } = item;
  // Zero-dollar bank lines are pathological (banks rarely emit them and
  // when they do the row is auto-resolved upstream); treating them as a
  // credit is harmless because the Exclude tile covers both signs.
  const isCredit = bt.amount >= 0;
  const tiles = isCredit ? CREDIT_TILES : DEBIT_TILES;

  const suggestedTile = suggestedToTile(suggestedKind(item));

  // SortInbox DTO lacks BSW-005 suggested-contact hints — pass null so
  // the panel's contact picker starts empty rather than carrying stale
  // hints from a different surface.
  const panelRow: BankTransactionPanelRow = {
    id: bt.id,
    date: bt.date,
    amount: bt.amount,
    description: bt.description,
    sourceLabel: bt.bankAccountName,
    concurrencyVersion: bt.concurrencyVersion,
    suggestedContactId: null,
    suggestedPaymentMethod: null,
    suggestedReferenceNumber: null,
  };

  return (
    <div data-testid="bank-line-body" className="flex flex-col gap-3">
      <div
        data-testid="bank-line-summary"
        className="rounded-md border bg-muted/30 p-3 text-sm"
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-xs text-muted-foreground">
            {bt.date}
          </span>
          <span className="font-semibold">
            {formatAmount(bt.amount)}
          </span>
          <span className="text-foreground">{bt.description}</span>
          <span className="ml-auto rounded bg-background px-2 py-0.5 text-xs text-muted-foreground">
            {bt.bankAccountName}
          </span>
        </div>
        {/* #591 — the shared bank-line info-header carries the "Not yet cleared"
            marker, the lingering-duplicate hint, and the straggler tag, the same
            three signals the ledger row + its expanded detail now render. */}
        <BankLineInfoHeader id={item.id} bt={bt} surface="popup" className="mt-2" />
      </div>

      {/* ADR-014: flex-wrap (not grid) so tiles stack full-width at font-scale
          1.3. Money tiles lead (flex-1, prominent); the muted Exclude off-ramp
          trails after a faint divider. a11y on every tile: min-h-11 (44pt),
          focus ring, aria-pressed. */}
      <div className="flex flex-wrap items-stretch gap-2">
        {tiles
          .filter((t) => t.tile !== 'exclude')
          .map((t) => {
            const isPicked = pickedTile === t.tile;
            const isSuggested = suggestedTile === t.tile;
            return (
              <button
                key={t.tile}
                type="button"
                data-testid={`triage-tile-${t.tile}`}
                data-suggested={isSuggested ? 'true' : 'false'}
                data-picked={isPicked ? 'true' : 'false'}
                aria-pressed={isPicked}
                onClick={() => onPickTile(t.tile)}
                className={cn(
                  'flex min-h-11 flex-1 basis-32 flex-col items-center justify-center gap-1 rounded-md border px-3 py-3 text-sm font-medium transition-colors',
                  'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isPicked
                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary'
                    : isSuggested
                      ? 'border-primary/60 bg-primary/5'
                      : 'border-border',
                )}
              >
                <span>{t.label}</span>
                {t.keyHint && (
                  <span className="text-xs opacity-70" aria-hidden="true">
                    {t.keyHint}
                  </span>
                )}
              </button>
            );
          })}

        {/* Faint divider separating the money paths from the muted off-ramp. */}
        <div aria-hidden="true" className="w-px self-stretch bg-border/60" />

        {(() => {
          const isPicked = pickedTile === 'exclude';
          return (
            <button
              type="button"
              data-testid="triage-tile-exclude"
              data-suggested={suggestedTile === 'exclude' ? 'true' : 'false'}
              data-picked={isPicked ? 'true' : 'false'}
              aria-pressed={isPicked}
              onClick={() => onPickTile('exclude')}
              className={cn(
                'flex min-h-11 flex-none flex-col items-center justify-center gap-1 rounded-md border border-dashed px-3 py-2 text-sm font-medium text-foreground transition-colors',
                'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                // Picked = muted fill + ✓ (never the primary fill the money
                // tiles use — Exclude stays visually secondary).
                isPicked ? 'border-foreground/40 bg-muted' : 'border-border',
              )}
            >
              <span className="flex items-center gap-1">
                {isPicked && <span aria-hidden="true">✓</span>}
                {EXCLUDE_TILE.label}
              </span>
              {EXCLUDE_TILE.keyHint && (
                <span className="text-xs opacity-70" aria-hidden="true">
                  {EXCLUDE_TILE.keyHint}
                </span>
              )}
            </button>
          );
        })()}
      </div>

      {pickedTile && (
        <BankTransactionClassificationPanel
          // Remount per tile so the form seeds from the hint only on the expense path.
          key={pickedTile}
          row={panelRow}
          onClose={onClose}
          onClassified={onClassified}
          initialSelectedType={tileToSelectedType(pickedTile)}
          hideTypeToggle
          classificationHint={pickedTile === 'expense' ? bt.classificationHint : null}
        />
      )}
    </div>
  );
}

